/*
 * td-collection-browse
 * ---------------------------------------------------------------------------
 * Progressive enhancement over the server-rendered {% paginate %} output in
 * sections/main-collection-product-grid.liquid.
 *
 * Three modes, driven by the section's `pagination_mode` setting and passed in
 * on `data-mode`:
 *   numbered   - the element is not rendered at all, nothing here runs.
 *   load_more  - numbered links are hidden, a "Load more" button appends the
 *                next page on click.
 *   infinite   - every remaining page appends automatically as a sentinel
 *                scrolls into view, until the collection runs out. The footer
 *                is only reachable at the end; pick load_more if it must be.
 *                The manual button only appears here if a fetch fails.
 *
 * Things worth knowing before editing:
 *
 * - This element lives INSIDE #ProductGridContainer. assets/facets.js replaces
 *   that container's innerHTML wholesale on every filter/sort change, which
 *   destroys this element and parses a fresh one. That is deliberate: all state
 *   lives on the instance and connectedCallback re-reads it from the new
 *   server-rendered data- attributes, so filtering cannot leave a stale page
 *   cursor behind. Never move initialisation to a one-time DOMContentLoaded
 *   binding.
 *
 * - Pages are fetched as section renders (`?page=N&section_id=...`), the same
 *   trick facets.js uses, so the response is just this section rather than a
 *   whole document.
 *
 * - The numbered pagination markup is never removed, only hidden with a class,
 *   so `?page=N` links with rel="next"/rel="prev" stay in the source for
 *   crawlers.
 *
 * - Appended cards need no re-initialisation. Star ratings, badges and stickers
 *   are rendered by Liquid, and quick add is a <modal-opener> custom element
 *   that upgrades itself the moment it is inserted into the live document.
 */

const TD_COLLECTION_BROWSE = {
  // Fetch the sentinel this far ahead of the viewport.
  ROOT_MARGIN: '600px 0px',
  // Most pages we are willing to back-fill when someone lands on ?page=N
  // directly. Beyond this the numbered pagination is left visible instead.
  RESTORE_MAX_PAGES: 10,
  // How long a remembered scroll position stays valid, in milliseconds.
  SCROLL_TTL: 30 * 60 * 1000,
  STORAGE_PREFIX: 'td-collection-browse:',
};

class TdCollectionBrowse extends HTMLElement {
  constructor() {
    super();
    this.onSentinelIntersect = this.onSentinelIntersect.bind(this);
    this.onLoadMoreClick = this.onLoadMoreClick.bind(this);
    this.onPageHide = this.onPageHide.bind(this);
    this.initialised = false;
  }

  connectedCallback() {
    if (this.initialised) return;

    this.mode = this.dataset.mode || 'numbered';
    if (this.mode !== 'infinite' && this.mode !== 'load_more') return;

    this.grid = this.querySelector('[data-td-browse-grid]');
    this.sentinel = this.querySelector('[data-td-browse-sentinel]');
    this.loadMoreButton = this.querySelector('[data-td-browse-more]');
    this.statusRegion = this.querySelector('[data-td-browse-status]');
    if (!this.grid) return;

    this.initialised = true;

    this.sectionId = this.dataset.sectionId || '';
    this.firstPage = this.readNumber('currentPage', 1);
    this.totalPages = this.readNumber('totalPages', 1);
    this.totalProducts = this.readNumber('totalProducts', 0);
    this.announcementTemplate = this.dataset.announcementTemplate || '';

    this.lowestPage = this.firstPage;
    this.highestPage = this.firstPage;
    this.autoLoadStopped = false;
    this.loading = false;

    this.classList.add('td-collection-browse--enhanced');
    this.loadMoreButton?.addEventListener('click', this.onLoadMoreClick);
    window.addEventListener('pagehide', this.onPageHide);

    // Only take the numbered links away once page 1 is actually on screen,
    // otherwise someone who landed on ?page=5 would have no way back.
    if (this.lowestPage === 1) this.classList.add('td-collection-browse--hide-pagination');

    if (this.sentinel && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.onSentinelIntersect, {
        rootMargin: TD_COLLECTION_BROWSE.ROOT_MARGIN,
      });
    }

    // updateControls() attaches the observer via refreshObserver().
    this.updateControls();
    this.restorePreviousPages();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.loadMoreButton?.removeEventListener('click', this.onLoadMoreClick);
    window.removeEventListener('pagehide', this.onPageHide);
  }

  readNumber(key, fallback) {
    const value = parseInt(this.dataset[key], 10);
    return Number.isNaN(value) ? fallback : value;
  }

  /* ----------------------------------------------------------------- state */

  get hasNextPage() {
    return this.highestPage < this.totalPages;
  }

  get canAutoLoad() {
    return this.mode === 'infinite' && !this.autoLoadStopped;
  }

  /* -------------------------------------------------------------- fetching */

  pageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  async fetchPage(page) {
    const response = await fetch(this.pageUrl(page));
    if (!response.ok) throw new Error(`td-collection-browse: page ${page} responded ${response.status}`);
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const grid = parsed.querySelector('[data-td-browse-grid]');
    return grid ? Array.from(grid.children) : [];
  }

  /* ------------------------------------------------------------- appending */

  async loadNextPage(isAuto) {
    if (this.loading || !this.hasNextPage) return;

    const page = this.highestPage + 1;
    this.setLoading(true);

    try {
      const items = await this.fetchPage(page);
      if (!items.length) {
        // The collection shrank underneath us; stop rather than loop.
        this.totalPages = this.highestPage;
        return;
      }

      this.grid.append(...items);
      this.highestPage = page;

      this.syncUrl();
      this.announce();

      // Keyboard users pressed a button and expect to land on what it produced.
      if (!isAuto) items[0].querySelector('a')?.focus({ preventScroll: true });
    } catch (error) {
      console.error(error);
      this.setError();
    } finally {
      this.setLoading(false);
      this.updateControls();
    }
  }

  /**
   * Someone loaded ?page=N directly — a reload, a shared link, or the back
   * button after a product page. Pull pages N-1 … 1 in and prepend them so the
   * list reads as one continuous grid again, correcting scroll by the height
   * each insertion adds so the viewport never jumps.
   */
  async restorePreviousPages() {
    const missing = this.firstPage - 1;
    if (missing < 1 || missing > TD_COLLECTION_BROWSE.RESTORE_MAX_PAGES) return;

    const remembered = this.readRememberedScroll();

    // Take scroll back off the browser for the duration — it restores
    // asynchronously around load and would fight the corrections below.
    const previousRestoration = window.history.scrollRestoration;
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

    this.setLoading(true);

    try {
      for (let page = this.firstPage - 1; page >= 1; page -= 1) {
        const items = await this.fetchPage(page);
        if (!items.length) break;

        const scrollBefore = window.scrollY;
        const heightBefore = document.documentElement.scrollHeight;
        this.grid.prepend(...items);
        const added = document.documentElement.scrollHeight - heightBefore;
        window.scrollTo(0, scrollBefore + added);

        this.lowestPage = page;
      }

      if (this.lowestPage === 1) this.classList.add('td-collection-browse--hide-pagination');
      if (remembered !== null) window.scrollTo(0, remembered);
    } catch (error) {
      console.error(error);
    } finally {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = previousRestoration || 'auto';
      }
      this.setLoading(false);
      this.updateControls();
    }
  }

  /* --------------------------------------------------------------- history */

  /** Collection URL without `page`, used to key the remembered scroll offset. */
  storageKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    return `${TD_COLLECTION_BROWSE.STORAGE_PREFIX}${url.pathname}${url.search}`;
  }

  /**
   * Keep `page` in the address bar as pages append, so a reload or a back
   * navigation from a product page comes back to the same depth. Existing
   * history state is preserved because facets.js keeps its search params there.
   */
  syncUrl() {
    const url = new URL(window.location.href);
    if (this.highestPage > 1) {
      url.searchParams.set('page', String(this.highestPage));
    } else {
      url.searchParams.delete('page');
    }
    const state = Object.assign({}, window.history.state);
    window.history.replaceState(state, '', `${url.pathname}${url.search}${url.hash}`);
  }

  onPageHide() {
    if (this.lowestPage === this.firstPage && this.highestPage === this.firstPage && this.firstPage === 1) return;
    try {
      sessionStorage.setItem(
        this.storageKey(),
        JSON.stringify({ scrollY: Math.round(window.scrollY), savedAt: Date.now() })
      );
    } catch (error) {
      // Private browsing / quota. A missing scroll offset is not worth failing over.
    }
  }

  readRememberedScroll() {
    let raw = null;
    try {
      raw = sessionStorage.getItem(this.storageKey());
      sessionStorage.removeItem(this.storageKey());
    } catch (error) {
      return null;
    }
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.savedAt > TD_COLLECTION_BROWSE.SCROLL_TTL) return null;
      return typeof parsed.scrollY === 'number' ? parsed.scrollY : null;
    } catch (error) {
      return null;
    }
  }

  /* ---------------------------------------------------------------- events */

  onSentinelIntersect(entries) {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    if (!this.canAutoLoad || !this.hasNextPage || this.loading) return;
    this.loadNextPage(true);
  }

  onLoadMoreClick(event) {
    event.preventDefault();
    this.loadNextPage(false);
  }

  /* ------------------------------------------------------------------ view */

  setLoading(isLoading) {
    this.loading = isLoading;
    this.classList.toggle('td-collection-browse--loading', isLoading);
    if (!this.loadMoreButton) return;
    this.loadMoreButton.classList.toggle('loading', isLoading);
    this.loadMoreButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    this.loadMoreButton.disabled = isLoading;
  }

  setError() {
    this.classList.add('td-collection-browse--errored');
    // Stop auto-loading so a failing endpoint is not hammered on every scroll;
    // the Load more button takes over as a manual retry.
    this.autoLoadStopped = true;
  }

  updateControls() {
    const showButton = this.hasNextPage && !this.canAutoLoad;
    if (this.loadMoreButton) this.loadMoreButton.hidden = !showButton;
    if (this.sentinel) this.sentinel.hidden = !this.hasNextPage;
    this.refreshObserver();
  }

  /**
   * An IntersectionObserver only reports threshold crossings. If an appended
   * page is shorter than the root margin the sentinel never leaves the
   * observed band, so no further callback arrives and auto-loading stalls
   * until the next scroll. Re-observing delivers a fresh initial report of the
   * current state, which closes that gap without polling.
   */
  refreshObserver() {
    if (!this.observer || !this.sentinel) return;
    this.observer.unobserve(this.sentinel);
    if (this.canAutoLoad && this.hasNextPage) this.observer.observe(this.sentinel);
  }

  /** Announce the new total to screen readers via the polite live region. */
  announce() {
    if (!this.statusRegion || !this.announcementTemplate) return;
    const shown = this.grid.querySelectorAll('.product-grid__item:not(.product-grid__item-banner)').length;
    this.statusRegion.textContent = this.announcementTemplate
      .replace('[[shown]]', shown)
      .replace('[[total]]', this.totalProducts);
  }
}

if (!customElements.get('td-collection-browse')) {
  customElements.define('td-collection-browse', TdCollectionBrowse);
}
